/**
 * Text creation flow, step 1: the source (handoff §3, §19.2).
 *
 * Paste, a file (drop on desktop) or a photo (an image on desktop; read on the
 * device by the app's existing OCR) fills one editable text area. «Далее» sends
 * the body to the server's analysis; nothing is stored. Every limit shown here
 * is the server's (GET /texts/limits).
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Camera, ClipboardPaste, ScanText, Upload } from 'lucide-react'
import { useLocale } from '../i18n/context'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { useAnalyseText, useExtractTextFile, useTextLimits } from '../hooks/useTexts'
import { useTextFlowStore } from '../stores/textFlowStore'
import { useAuthStore, selectUser } from '../stores/authStore'
import { trackEvent } from '../utils/analytics'
import { apiErrorCode, apiErrorDetail } from '../utils/textFlowErrors'
import { fileUploadErrorMessage } from '../utils/fileUploadError'
import { textExtractErrorMessage } from '../utils/textExtractError'
import { recognize } from '../services/ocr'
import { ImageDecodeError, prepareImageForOcr } from '../services/ocrPrepare'
import { routeOcrResult } from '../services/ocrRouter'
import FlowFrame from '../components/texts/FlowFrame'
import { GhostButton, PrimaryButton, SecondaryPill } from '../components/texts/ui'
import LeaveConfirm from '../components/texts/LeaveConfirm'
import { useFlowOrigin } from '../components/texts/useFlowOrigin'

function wordCount(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0
}

function clipboardImage(data: DataTransfer | null): File | null {
  if (!data) return null
  for (const item of Array.from(data.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) return item.getAsFile()
  }
  return null
}

type OcrPhase = 'idle' | 'downloading' | 'recognizing'

export default function TextFlowSourcePage() {
  const { tr } = useLocale()
  const tf = tr.textFlow
  const ti = tr.textImport
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const user = useAuthStore(selectUser)
  const flow = useTextFlowStore()
  const limits = useTextLimits().data
  const analyse = useAnalyseText()
  const extractFile = useExtractTextFile()
  const { exit, backLabel } = useFlowOrigin()

  // A reload (or a deep link) arrives with no entry: start the neutral one.
  useEffect(() => {
    if (!useTextFlowStore.getState().entry) useTextFlowStore.getState().start('essay', '/library')
  }, [])
  const entry = flow.entry ?? 'essay'
  const title = entry === 'words' ? tf.entryWords : tf.entryEssay

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const ocrRun = useRef(0)
  const [error, setError] = useState<string | null>(null)
  const [ocrPhase, setOcrPhase] = useState<OcrPhase>('idle')
  const [ocrFraction, setOcrFraction] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [confirmLeave, setConfirmLeave] = useState(false)

  const body = flow.body
  const tooLong = !!limits && body.length > limits.max_chars
  const busy = ocrPhase !== 'idle' || extractFile.isPending || analyse.isPending
  const canNext = body.trim().length > 0 && !tooLong && !busy

  // ── Photo / image: on-device OCR, the app's existing pipeline ────────────
  async function runOcr(file: File, entryPoint: string) {
    trackEvent('ocr_initiated', { entry_point: entryPoint })
    const run = ++ocrRun.current
    setError(null)
    setOcrPhase('downloading')
    setOcrFraction(0)
    try {
      const prepared = await prepareImageForOcr(file)
      const result = await recognize(prepared, user?.target_language, ({ phase, progress }) => {
        if (ocrRun.current !== run) return
        setOcrPhase(phase === 'download' ? 'downloading' : 'recognizing')
        setOcrFraction(progress)
      })
      if (ocrRun.current !== run) return
      // Normalise the noise only; the destination is already this flow.
      const decision = routeOcrResult(result.text)
      if (decision.kind === 'empty') {
        trackEvent('ocr_failed', { reason: 'empty' })
        setError(tf.source.ocrNothing)
      } else {
        trackEvent('ocr_succeeded')
        flow.setBody(decision.text, 'photo')
      }
    } catch (err) {
      if (ocrRun.current !== run) return
      trackEvent('ocr_failed', { reason: 'recognition_error' })
      setError(err instanceof ImageDecodeError ? ti.ocrErrDecode : ti.ocrErrGeneric)
    } finally {
      if (ocrRun.current === run) setOcrPhase('idle')
    }
  }

  // ── File: the server extracts its text (same checks as text import) ──────
  function runFile(file: File) {
    setError(null)
    extractFile.mutate(file, {
      onSuccess: (data) => flow.setBody(data.text, 'file', data.filename),
      onError: (err) => {
        const code = apiErrorCode(err)
        setError(
          code === 'FILE_CORRUPTED'
            ? tf.source.fileUnreadable
            : fileUploadErrorMessage(err, file.name, ti),
        )
      },
    })
  }

  function takeFile(file: File | undefined, entryPoint: string) {
    if (!file) return
    if (file.type.startsWith('image/')) void runOcr(file, entryPoint)
    else runFile(file)
  }

  // ── Paste ────────────────────────────────────────────────────────────────
  async function pasteFromClipboard() {
    setError(null)
    try {
      const pasted = await navigator.clipboard.readText()
      if (!pasted) return
      const el = textareaRef.current
      const at = el ? [el.selectionStart, el.selectionEnd] : [body.length, body.length]
      flow.setBody(body.slice(0, at[0]) + pasted + body.slice(at[1]), 'typed')
    } catch {
      setError(tf.source.pasteFailed)
      textareaRef.current?.focus()
    }
  }

  // Ctrl+V with an image on the clipboard starts OCR (desktop). Plain text goes to the area.
  const takeFileRef = useRef(takeFile)
  useEffect(() => { takeFileRef.current = takeFile })
  useEffect(() => {
    if (!isDesktop) return
    function onPaste(e: ClipboardEvent) {
      const image = clipboardImage(e.clipboardData)
      if (!image) return
      e.preventDefault()
      takeFileRef.current(image, 'text_flow_paste')
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [isDesktop])

  // ── Next: analyse the body on the server ─────────────────────────────────
  function next() {
    if (!canNext) return
    setError(null)
    const state = useTextFlowStore.getState()
    if (state.analysis && state.analysedBody === body) {
      navigate('/texts/new/words')
      return
    }
    analyse.mutate(
      { text: body },
      {
        onSuccess: (data) => {
          useTextFlowStore.getState().setAnalysis(data, body)
          navigate('/texts/new/words')
        },
        onError: (err) => {
          const code = apiErrorCode(err)
          const max = Number(apiErrorDetail(err)?.max_chars ?? limits?.max_chars)
          if (code === 'TEXT_TOO_LONG' && max) setError(tf.source.tooLong(max))
          else if (code === 'RATE_LIMITED' || code === 'NLP_MODEL_UNAVAILABLE' || code === 'SERVICE_UNAVAILABLE') {
            setError(textExtractErrorMessage(err, ti))
          } else setError(tf.source.analyseFailed)
        },
      },
    )
  }

  function leave() {
    if (body.trim()) setConfirmLeave(true)
    else exit()
  }

  // ── Pieces ───────────────────────────────────────────────────────────────
  const fromImage = flow.source === 'photo'
  const fromFile = flow.source === 'file'
  const banner = fromImage
    ? (isDesktop ? tf.source.imageBanner : tf.source.photoBanner)
    : fromFile && flow.sourceName ? tf.source.fileBanner(flow.sourceName) : null
  const replaceLabel = fromImage
    ? (isDesktop ? tf.source.replaceImage : tf.source.replacePhoto)
    : tf.source.replaceFile
  const replace = () => (fromFile ? fileInputRef : imageInputRef).current?.click()

  const bannerEl = banner && (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, padding: 12, borderRadius: 8,
        background: 'var(--accent-soft)', color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.45,
      }}
    >
      <ScanText size={18} aria-hidden style={{ flexShrink: 0, color: 'var(--accent)' }} />
      <span style={{ flex: 1 }}>{banner}</span>
      {isDesktop && (
        <GhostButton onClick={replace} style={{ height: 28, padding: 0, fontSize: 13 }}>{replaceLabel}</GhostButton>
      )}
    </div>
  )

  const ocrEl = ocrPhase !== 'idle' && (
    <div role="status" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
      <div style={{ marginBottom: 6 }}>
        {ocrPhase === 'downloading' ? ti.ocrDownloading : ti.ocrReading}
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--mastery-track)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.round(ocrFraction * 100)}%`, height: '100%', background: 'var(--accent)' }} />
      </div>
    </div>
  )

  const textarea = (
    <textarea
      ref={textareaRef}
      value={body}
      onChange={(e) => { flow.setBody(e.target.value, 'typed'); setError(null) }}
      placeholder={dragging ? tf.source.dropHere : isDesktop ? `${tf.source.placeholder}\n${tf.source.placeholderDesktop}` : tf.source.placeholder}
      aria-label={title}
      spellCheck={false}
      style={{
        flex: 1, minHeight: isDesktop ? 360 : 240, width: '100%', resize: 'none', boxSizing: 'border-box',
        padding: isDesktop ? '20px 24px' : 16, borderRadius: 16, background: 'var(--bg-surface)',
        color: 'var(--text-primary)', fontFamily: 'var(--font-content)', fontSize: 18, lineHeight: 1.6,
        border: dragging ? '2px dashed var(--accent)' : '1px solid var(--border)', outline: 'none',
      }}
      onFocus={(e) => { e.currentTarget.style.border = '2px solid var(--accent)' }}
      onBlur={(e) => { e.currentTarget.style.border = dragging ? '2px dashed var(--accent)' : '1px solid var(--border)' }}
    />
  )

  const pillHeight = isDesktop ? 40 : 44
  const sources = (
    <div style={{ display: 'flex', gap: 8, ...(isDesktop ? {} : { width: '100%' }) }}>
      <SecondaryPill height={pillHeight} style={{ flex: isDesktop ? undefined : 1 }} onClick={pasteFromClipboard} disabled={busy}>
        <ClipboardPaste size={18} aria-hidden />{tf.source.paste}
      </SecondaryPill>
      <SecondaryPill height={pillHeight} style={{ flex: isDesktop ? undefined : 1 }} onClick={() => imageInputRef.current?.click()} disabled={busy}>
        <Camera size={18} aria-hidden />{isDesktop ? tf.source.image : tf.source.photo}
      </SecondaryPill>
      <SecondaryPill height={pillHeight} style={{ flex: isDesktop ? undefined : 1 }} onClick={() => fileInputRef.current?.click()} disabled={busy}>
        <Upload size={18} aria-hidden />{tf.source.file}
      </SecondaryPill>
    </div>
  )

  const words = wordCount(body)
  const errorEl = (error || tooLong) && (
    <p role="alert" style={{ margin: 0, display: 'flex', gap: 8, fontSize: 13, lineHeight: 1.45, color: 'var(--danger)' }}>
      <AlertCircle size={16} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
      <span>{error ?? tf.source.tooLong(limits!.max_chars)}</span>
    </p>
  )

  const nextLabel = analyse.isPending ? tf.source.analysing : tf.source.next

  return (
    <>
      <input
        ref={imageInputRef} type="file" accept="image/*" hidden aria-hidden tabIndex={-1}
        data-testid="text-flow-image-input"
        onChange={(e) => { takeFile(e.target.files?.[0], 'text_flow'); e.target.value = '' }}
      />
      <input
        ref={fileInputRef} type="file" accept=".txt,.docx,.pdf,.pptx" hidden aria-hidden tabIndex={-1}
        data-testid="text-flow-file-input"
        onChange={(e) => { takeFile(e.target.files?.[0], 'text_flow'); e.target.value = '' }}
      />
      <FlowFrame
        step={1}
        title={title}
        leading="close"
        onLeave={leave}
        backLabel={backLabel}
        subtitle={tf.stepSubtitle(1, tf.stepNames.source)}
        maxWidth={720}
        footer={
          isDesktop ? undefined : <PrimaryButton full disabled={!canNext} onClick={next}>{nextLabel}</PrimaryButton>
        }
      >
        <div
          onDragOver={isDesktop ? (e) => { e.preventDefault(); setDragging(true) } : undefined}
          onDragLeave={isDesktop ? () => setDragging(false) : undefined}
          onDrop={isDesktop ? (e) => {
            e.preventDefault()
            setDragging(false)
            takeFile(e.dataTransfer.files?.[0], 'text_flow_drop')
          } : undefined}
          style={{
            display: 'flex', flexDirection: 'column', gap: 16, flex: 1,
            padding: isDesktop ? '28px 0' : '20px 16px', boxSizing: 'border-box',
          }}
        >
          {bannerEl}
          {textarea}
          {!isDesktop && (fromImage || fromFile) && body && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-secondary)' }}>
              <span>{tf.source.wordCount(words)}</span>
              <GhostButton onClick={replace} style={{ height: 36, padding: 0, fontSize: 13 }}>{replaceLabel}</GhostButton>
            </div>
          )}
          {ocrEl}
          {extractFile.isPending && <p role="status" style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{tf.source.reading}</p>}
          {isDesktop ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {sources}
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)' }}>
                {body.trim() ? tf.source.wordCount(words) : tf.source.helperDesktopEmpty}
              </span>
              <PrimaryButton height={44} disabled={!canNext} onClick={next}>{nextLabel}</PrimaryButton>
            </div>
          ) : (
            <>
              {sources}
              {!(fromImage || fromFile) && (
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)' }}>{tf.source.helper}</p>
              )}
            </>
          )}
          {errorEl}
        </div>
      </FlowFrame>
      <LeaveConfirm open={confirmLeave} onStay={() => setConfirmLeave(false)} onLeave={() => { setConfirmLeave(false); exit() }} />
    </>
  )
}
